import type { ReactNode } from 'react'

import type { Mobility } from '../../api/transport'
import { Button } from '../../components/Button'
import { Icon } from '../../components/Icon'
import { PageBand, PageShell } from '../../components/PageShell'
import { EMERGENCY_NUMBER } from '../../lib/emergencyNumber'
import { useFocusOnMount } from './focus'
import { MOBILITY, SIGNS } from './services'
import { TransportHeader } from './TransportHeader'

/**
 * Medcor Transport, screen 1: the safety check (M34-T03, ADR-0063), shared by
 * the M34 holding page and the booking flow (M39-T09).
 *
 * Medcor Transport is a scheduled, NON-emergency service (spec §C.3, owner
 * Decision 2). This screen stops anyone with an emergency before a booking can
 * begin: "Yes — call 112 now" ends the flow, and NOTHING on the page leads back
 * into it. No "continue anyway", no override. The warning signs are an
 * information list, not inputs, so an emergency that is not on the list can
 * still stop the booking.
 *
 * The flow adds to it through the slots: the progress bar, the oxygen
 * question, its own recommendation line and its own rule for Continue.
 */
export function Gate({
  focusHeading,
  subtitle,
  progress,
  planned,
  onPlanned,
  mobility,
  onMobility,
  extra,
  notice,
  canContinue,
  onStop,
  onContinue,
  onBack,
}: {
  focusHeading: boolean
  subtitle: string
  /** Shown under the title (the flow's progress bar). */
  progress?: ReactNode
  planned: boolean
  onPlanned: (v: boolean) => void
  mobility: Mobility | null
  onMobility: (m: Mobility) => void
  /** Questions after mobility (the flow's oxygen question). */
  extra?: ReactNode
  /** The live line above Continue: the recommendation, or why not. */
  notice: ReactNode
  canContinue: boolean
  onStop: () => void
  onContinue: () => void
  /** Where the header's Back goes; by default, out of the flow. */
  onBack?: () => void
}): ReactNode {
  const heading = useFocusOnMount<HTMLHeadingElement>(focusHeading)

  return (
    <PageShell measure={640} as="main">
      <TransportHeader {...(onBack ? { onBack } : {})} />
      <PageBand
        className="flex flex-1 flex-col"
        contentClassName="flex flex-1 flex-col gap-4 px-4 py-5"
      >
        <div>
          <h1
            ref={heading}
            tabIndex={-1}
            className="font-display text-d-xs font-medium tracking-display text-ink-1 focus:outline-none"
          >
            Book transport
          </h1>
          <p className="mt-1 text-[13px] text-ink-3">{subtitle}</p>
        </div>

        {progress}

        <p className="rounded-r3 border border-line-1 bg-bg-soft px-3 py-2.5 text-[12px] leading-relaxed text-ink-2">
          Medcor Transport is for planned, non-emergency trips only. If someone
          needs emergency care, you cannot book transport here — call{' '}
          {EMERGENCY_NUMBER}.
        </p>

        <section
          aria-labelledby="gate-title"
          className="rounded-r3 border-[1.5px] border-sos bg-sos-bg px-3.5 py-3"
        >
          <h2 id="gate-title" className="text-[15px] font-semibold text-sos">
            This service is not for emergencies
          </h2>
          <p className="mt-1 text-[12.5px] text-ink-1">
            Is the passenger experiencing any of these right now?
          </p>
          <ul className="mt-2 list-disc space-y-0.5 pl-5 text-[12.5px] text-ink-1">
            {SIGNS.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
          {/* A tel: link, so on a phone one tap both dials and stops the
              booking. Always enabled: the list informs, it does not gate.
              Middle-click (auxclick) stops the booking too, or this tab would
              stay on the form while 112 opens in another. */}
          <a
            href={`tel:${EMERGENCY_NUMBER}`}
            onClick={onStop}
            onAuxClick={onStop}
            className="mt-3 flex items-center justify-center gap-2 rounded-r3 bg-sos px-3 py-2.5 text-[14px] font-semibold text-sos-bg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sos"
          >
            <Icon name="phone" size={16} /> Yes — call {EMERGENCY_NUMBER} now
          </a>
          <p className="mt-2 text-[11.5px] leading-relaxed text-sos">
            Or go straight to the nearest emergency department. Do not wait for
            a booking.
          </p>
        </section>

        <label className="flex items-start gap-2.5 rounded-r3 border border-line-2 px-3 py-2.5">
          <input
            type="checkbox"
            checked={planned}
            onChange={(e) => onPlanned(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 accent-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          />
          <span className="text-[13px] font-medium text-ink-1">
            No — this is a planned, non-emergency trip
          </span>
        </label>

        <fieldset
          disabled={!planned}
          aria-describedby="mobility-help"
          className="disabled:opacity-50"
        >
          <legend className="font-mono text-[10px] uppercase tracking-wide text-ink-3">
            How does the passenger get around?
          </legend>
          {/* Disabled radios are skipped by keyboard and screen readers, so
              the reason they are disabled has to be said, not just shown. */}
          <p id="mobility-help" className="mt-0.5 text-[12px] text-ink-3">
            {planned
              ? 'This decides which vehicle we can send.'
              : 'Tick “No” above first. This decides which vehicle we can send.'}
          </p>
          <div className="mt-2 flex flex-col gap-2">
            {MOBILITY.map((m) => (
              <label
                key={m.value}
                className={`flex items-start gap-2.5 rounded-r3 border px-3 py-2.5 ${
                  mobility === m.value
                    ? 'border-accent bg-accent-bg'
                    : 'border-line-2 bg-bg-app'
                }`}
              >
                <input
                  type="radio"
                  name="mobility"
                  value={m.value}
                  checked={mobility === m.value}
                  onChange={() => onMobility(m.value)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                />
                <span className="text-[13px] font-medium text-ink-1">
                  {m.label}
                  <span className="block text-[11.5px] font-normal text-ink-3">
                    {m.sub}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        {extra}

        <p
          aria-live="polite"
          className="min-h-[1.25rem] text-[12px] leading-relaxed text-ink-2"
        >
          {notice}
        </p>

        <Button
          kind="primary"
          full
          disabled={!canContinue}
          onClick={onContinue}
        >
          Continue
        </Button>
      </PageBand>
    </PageShell>
  )
}
